/**
 * PolaroidsAdmin — admin UI to create/edit/delete Polaroid catalog items
 * across the 3 categories (Regals, Mercat, Classes particulars).
 * Each item is targeted at one or many communities and has multilingual
 * title/intro/body, an image gallery and optional URL/email/WhatsApp links.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { convertImgurUrl } from '../../utils/imgurUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const CATEGORIES = [
  { key: 'gifts',   label: '🎁 Regals i Premis' },
  { key: 'market',  label: '🛒 Mercat' },
  { key: 'lessons', label: '⛳ Classes particulars' },
];

const COMMUNITIES = [
  { key: 'nuts',  label: '🥜 Nuts' },
  { key: 'caps',  label: '🧢 Caps' },
  { key: 'shark', label: '🦈 Shark' },
];

const LANGS = ['ca', 'es', 'en', 'fr'];

const SUBCATEGORIES = ['palos', 'roba', 'bosses', 'accessoris', 'electronica', 'altres'];

const emptyMultilingual = () => ({ ca: '', es: '', en: '', fr: '' });

const emptyItem = (category) => ({
  category,
  communities: ['nuts'],
  title: emptyMultilingual(),
  intro: emptyMultilingual(),
  body: emptyMultilingual(),
  images: [],
  links: { url: '', email: '', whatsapp: '', custom: [] },
  price: null,
  state: 'active',
  subcategory: null,
  featured: false,
});

const stripEmpty = (item) => {
  const out = { ...item };
  // Clean links → remove empty fields
  const lk = { ...item.links };
  if (!lk.url) lk.url = null;
  if (!lk.email) lk.email = null;
  if (!lk.whatsapp) lk.whatsapp = null;
  lk.custom = (lk.custom || []).filter((c) => c.url);
  out.links = lk;
  if (!out.price || !out.price.amount) out.price = null;
  if (!out.subcategory) out.subcategory = null;
  return out;
};

export const PolaroidsAdmin = () => {
  const [activeCat, setActiveCat] = useState('gifts');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {...item}

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API_URL}/api/zona-pro/polaroid/items`, {
        params: { category: activeCat },
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(r.data?.items || []);
    } catch {
      toast.error('No s\'han pogut carregar els ítems');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [activeCat]);

  const handleSave = async (item) => {
    const token = localStorage.getItem('token');
    const payload = stripEmpty(item);
    try {
      if (item.id) {
        await axios.put(`${API_URL}/api/zona-pro/polaroid/items/${item.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Ítem actualitzat');
      } else {
        await axios.post(`${API_URL}/api/zona-pro/polaroid/items`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Ítem creat');
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Segur que vols eliminar aquest ítem?')) return;
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API_URL}/api/zona-pro/polaroid/items/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Eliminat');
      await load();
    } catch {
      toast.error('Error en eliminar');
    }
  };

  return (
    <div className="space-y-4" data-testid="polaroids-admin">
      {/* Category tabs */}
      <div className="flex flex-wrap gap-2">
        {CATEGORIES.map((c) => (
          <Button
            key={c.key}
            variant={activeCat === c.key ? 'default' : 'outline'}
            onClick={() => setActiveCat(c.key)}
            size="sm"
            data-testid={`polaroids-tab-${c.key}`}
          >
            {c.label}
          </Button>
        ))}
        <div className="flex-1" />
        <Button
          onClick={() => setEditing(emptyItem(activeCat))}
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
          size="sm"
          data-testid="polaroids-create-btn"
        >
          + Nou ítem
        </Button>
      </div>

      {loading ? (
        <div className="p-8 text-center text-slate-500">Carregant…</div>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-slate-500">
            <p>Encara no hi ha ítems en aquesta categoria.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((it) => (
            <Card key={it.id} data-testid={`polaroid-admin-item-${it.id}`}>
              <CardContent className="p-4 flex gap-4">
                <div className="w-24 h-24 flex-shrink-0 bg-slate-100 rounded overflow-hidden">
                  {it.images?.[0] ? (
                    <img src={it.images[0]} alt="" className="w-full h-full object-cover" />
                  ) : <div className="w-full h-full flex items-center justify-center text-2xl text-slate-400">🖼️</div>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {it.featured && <span className="text-[10px] uppercase tracking-wider bg-amber-100 text-amber-800 px-1.5 rounded">★ Destacat</span>}
                    {it.state !== 'active' && <span className="text-[10px] uppercase tracking-wider bg-slate-200 text-slate-700 px-1.5 rounded">{it.state}</span>}
                    {it.communities?.map((c) => (
                      <span key={c} className="text-[10px] uppercase tracking-wider text-slate-500">{c}</span>
                    ))}
                  </div>
                  <div className="font-medium mt-1 truncate">{it.title?.ca || it.title?.es || '(sense títol)'}</div>
                  <div className="text-xs text-slate-500 line-clamp-2">{it.intro?.ca || it.intro?.es}</div>
                  <div className="mt-2 flex gap-2 text-[11px] text-slate-500">
                    {it.price && <span>{it.price.amount} {it.price.currency}</span>}
                    <span>👍 {it.likes_count || 0}</span>
                    <span>📷 {it.images?.length || 0}</span>
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <Button size="sm" variant="outline" onClick={() => setEditing(it)} data-testid={`polaroid-admin-edit-${it.id}`}>Editar</Button>
                  <Button size="sm" variant="outline" onClick={() => handleDelete(it.id)} className="text-red-600">Eliminar</Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing?.id ? 'Editar ítem' : 'Nou ítem'}</DialogTitle>
          </DialogHeader>
          {editing && <PolaroidItemForm item={editing} onChange={setEditing} onSave={handleSave} onCancel={() => setEditing(null)} />}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// --- Form component ---
const PolaroidItemForm = ({ item, onChange, onSave, onCancel }) => {
  const [activeLang, setActiveLang] = useState('ca');
  const [newImage, setNewImage] = useState('');
  const [newCustomLabel, setNewCustomLabel] = useState('');
  const [newCustomUrl, setNewCustomUrl] = useState('');

  const setMl = (field, val) => onChange({ ...item, [field]: { ...item[field], [activeLang]: val } });
  const toggleCommunity = (c) => {
    const has = item.communities.includes(c);
    onChange({ ...item, communities: has ? item.communities.filter((x) => x !== c) : [...item.communities, c] });
  };
  const addImage = () => {
    if (!newImage.trim()) return;
    // Auto-convert Imgur page URLs (https://imgur.com/XYZ) → direct (https://i.imgur.com/XYZ.jpg)
    const url = convertImgurUrl(newImage.trim());
    onChange({ ...item, images: [...item.images, url] });
    setNewImage('');
  };
  const removeImage = (i) => onChange({ ...item, images: item.images.filter((_, idx) => idx !== i) });
  const moveImage = (i, dir) => {
    const ar = [...item.images];
    const j = i + dir;
    if (j < 0 || j >= ar.length) return;
    [ar[i], ar[j]] = [ar[j], ar[i]];
    onChange({ ...item, images: ar });
  };
  const setLink = (k, v) => onChange({ ...item, links: { ...item.links, [k]: v } });
  const addCustomLink = () => {
    if (!newCustomUrl.trim()) return;
    onChange({ ...item, links: { ...item.links, custom: [...(item.links.custom || []), { label: newCustomLabel.trim() || 'Enllaç', url: newCustomUrl.trim() }] } });
    setNewCustomLabel(''); setNewCustomUrl('');
  };
  const removeCustom = (i) => onChange({ ...item, links: { ...item.links, custom: item.links.custom.filter((_, idx) => idx !== i) } });

  const isMarket = item.category === 'market';

  return (
    <div className="space-y-5">
      {/* Communities */}
      <div>
        <Label>Comunitats objectiu *</Label>
        <div className="mt-2 flex flex-wrap gap-2">
          {COMMUNITIES.map((c) => {
            const checked = item.communities.includes(c.key);
            return (
              <button
                key={c.key}
                type="button"
                onClick={() => toggleCommunity(c.key)}
                className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                  checked ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white border-slate-300 text-slate-700'
                }`}
                data-testid={`polaroid-form-community-${c.key}`}
              >
                {c.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Lang tabs */}
      <div className="flex gap-2 border-b">
        {LANGS.map((l) => (
          <button
            key={l}
            type="button"
            onClick={() => setActiveLang(l)}
            className={`px-3 py-1.5 text-sm uppercase tracking-wider ${activeLang === l ? 'border-b-2 border-emerald-600 font-bold' : 'text-slate-500'}`}
          >
            {l}
          </button>
        ))}
      </div>

      <div>
        <Label>Títol ({activeLang})</Label>
        <Input value={item.title[activeLang] || ''} onChange={(e) => setMl('title', e.target.value)} data-testid="polaroid-form-title" />
      </div>
      <div>
        <Label>Entradeta / lead ({activeLang})</Label>
        <Input value={item.intro[activeLang] || ''} onChange={(e) => setMl('intro', e.target.value)} />
      </div>
      <div>
        <Label>Cos / descripció ({activeLang})</Label>
        <Textarea rows={5} value={item.body[activeLang] || ''} onChange={(e) => setMl('body', e.target.value)} />
      </div>

      {/* Images */}
      <div>
        <Label>Galeria d&apos;imatges (URLs Cloudinary/Imgur)</Label>
        <div className="mt-2 flex gap-2">
          <Input placeholder="https://..." value={newImage} onChange={(e) => setNewImage(e.target.value)} />
          <Button type="button" onClick={addImage} size="sm">Afegir</Button>
        </div>
        {item.images.length > 0 && (
          <div className="mt-3 grid grid-cols-3 sm:grid-cols-4 gap-2">
            {item.images.map((img, i) => (
              <div key={i} className="relative group rounded overflow-hidden border">
                <img src={img} alt="" className="w-full aspect-square object-cover" />
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-1 text-white text-xs">
                  <div className="flex gap-1">
                    <button type="button" onClick={() => moveImage(i, -1)} className="px-2">◀</button>
                    <button type="button" onClick={() => moveImage(i, 1)} className="px-2">▶</button>
                  </div>
                  <button type="button" onClick={() => removeImage(i)} className="px-2 text-red-300">Eliminar</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Links */}
      <div className="space-y-2 border-t pt-4">
        <Label className="text-base">Enllaços</Label>
        <Input placeholder="URL web (opcional)" value={item.links.url || ''} onChange={(e) => setLink('url', e.target.value)} />
        <Input placeholder="Email (opcional, s'usarà com mailto:)" value={item.links.email || ''} onChange={(e) => setLink('email', e.target.value)} />
        <Input placeholder="WhatsApp +34600... (opcional, format internacional)" value={item.links.whatsapp || ''} onChange={(e) => setLink('whatsapp', e.target.value)} />
        {/* Custom links */}
        {item.links.custom?.length > 0 && (
          <div className="space-y-1">
            {item.links.custom.map((c, i) => (
              <div key={i} className="flex items-center gap-2 text-sm bg-slate-50 px-2 py-1 rounded">
                <span className="font-medium">{c.label}:</span>
                <span className="text-slate-500 truncate flex-1">{c.url}</span>
                <button type="button" onClick={() => removeCustom(i)} className="text-red-600 text-xs">×</button>
              </div>
            ))}
          </div>
        )}
        <div className="flex gap-2">
          <Input placeholder="Etiqueta extra" value={newCustomLabel} onChange={(e) => setNewCustomLabel(e.target.value)} className="w-1/3" />
          <Input placeholder="URL extra" value={newCustomUrl} onChange={(e) => setNewCustomUrl(e.target.value)} />
          <Button type="button" size="sm" onClick={addCustomLink}>+ Extra</Button>
        </div>
      </div>

      {/* Market-only fields */}
      {isMarket && (
        <div className="space-y-3 border-t pt-4">
          <Label className="text-base">🛒 Camps de Mercat</Label>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Preu</Label>
              <Input type="number" step="0.01" value={item.price?.amount ?? ''}
                onChange={(e) => onChange({ ...item, price: e.target.value ? { amount: parseFloat(e.target.value), currency: item.price?.currency || 'EUR' } : null })} />
            </div>
            <div>
              <Label className="text-xs">Moneda</Label>
              <Input value={item.price?.currency || 'EUR'} onChange={(e) => onChange({ ...item, price: { ...(item.price || { amount: 0 }), currency: e.target.value } })} />
            </div>
            <div>
              <Label className="text-xs">Estat</Label>
              <select value={item.state} onChange={(e) => onChange({ ...item, state: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm">
                <option value="active">Actiu</option>
                <option value="reserved">Reservat</option>
                <option value="sold">Venut</option>
                <option value="archived">Arxivat</option>
              </select>
            </div>
            <div>
              <Label className="text-xs">Subcategoria</Label>
              <select value={item.subcategory || ''} onChange={(e) => onChange({ ...item, subcategory: e.target.value || null })} className="w-full border rounded px-2 py-1.5 text-sm">
                <option value="">— Cap —</option>
                {SUBCATEGORIES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Featured toggle */}
      <div className="flex items-center gap-2 pt-2 border-t">
        <input id="featured" type="checkbox" checked={item.featured} onChange={(e) => onChange({ ...item, featured: e.target.checked })} />
        <Label htmlFor="featured" className="cursor-pointer">★ Destacat (es mostra primer al catàleg)</Label>
      </div>

      {/* Save / Cancel */}
      <div className="flex justify-end gap-2 pt-3 border-t">
        <Button variant="outline" onClick={onCancel}>Cancel·lar</Button>
        <Button onClick={() => onSave(item)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="polaroid-form-save">
          {item.id ? 'Actualitzar' : 'Crear'}
        </Button>
      </div>
    </div>
  );
};

export default PolaroidsAdmin;
